import { Component } from '@theme/component';
import {
  enqueueCartMutation,
  requestCartJson,
  collectCartSectionIds,
  applyCartSectionsResponse,
  setCartItemsLocked,
} from '@theme/cart-section-updater';

/**
 * @param {number} line
 * @param {boolean} updating
 */
function setLineItemUpdating(line, updating) {
  const row = document.querySelector(
    `.cart-items__table-row[data-cart-line="${CSS.escape(String(line))}"]`
  );
  row?.classList.toggle('cart-items__table-row--updating', updating);
}

/**
 * @param {object} params
 * @param {number} params.variantId
 * @param {number} params.quantity
 * @param {number | null | undefined} params.sellingPlanId
 * @param {string} params.sections
 * @returns {Promise<{ ok: boolean, data?: object, sections?: Record<string, string> }>}
 */
async function addCartLine({ variantId, quantity, sellingPlanId, sections }) {
  const addBody = {
    id: variantId,
    quantity,
    sections,
    sections_url: window.location.pathname,
  };

  if (sellingPlanId) {
    addBody.selling_plan = sellingPlanId;
  }

  const addData = await requestCartJson(Theme.routes.cart_add_url, addBody);

  if (addData.status) {
    return { ok: false, data: addData };
  }

  return {
    ok: true,
    data: addData,
    sections: addData.sections,
  };
}

/**
 * @param {object} params
 * @param {number} params.line
 * @param {number} params.variantId
 * @param {number} params.quantity
 * @param {number | null | undefined} params.sellingPlanId
 * @param {string} params.sections
 * @returns {Promise<{ ok: boolean, data?: object, sections?: Record<string, string> }>}
 */
async function swapCartLineSellingPlan({
  line,
  variantId,
  quantity,
  sellingPlanId,
  sections,
}) {
  await requestCartJson(Theme.routes.cart_change_url, {
    line,
    quantity: 0,
    sections,
    sections_url: window.location.pathname,
  });

  return addCartLine({ variantId, quantity, sellingPlanId, sections });
}

/**
 * @param {HTMLElement} component
 * @param {object} params
 * @param {string} params.source
 * @param {number} params.line
 * @param {number} params.variantId
 * @param {number} params.quantity
 * @param {number | null | undefined} params.sellingPlanId
 * @param {number | null | undefined} [params.rollbackSellingPlanId]
 * @param {boolean} [params.rollbackOneTime]
 * @param {() => void} [params.onErrorReset]
 */
async function runCartSellingPlanUpdate(
  component,
  {
    source,
    line,
    variantId,
    quantity,
    sellingPlanId,
    rollbackSellingPlanId,
    rollbackOneTime = false,
    onErrorReset,
  }
) {
  const cartItemsEl = component.closest('cart-items-component');
  const sectionId = cartItemsEl?.dataset?.sectionId;

  if (!sectionId) return;

  const sectionsParam = collectCartSectionIds(sectionId);

  setLineItemUpdating(line, true);
  setCartItemsLocked(true);

  try {
    const result = await swapCartLineSellingPlan({
      line,
      variantId,
      quantity,
      sellingPlanId,
      sections: sectionsParam,
    });

    if (!result.ok) {
      console.error('Cart selling plan update failed', result.data?.description);
      onErrorReset?.();

      if (rollbackSellingPlanId != null || rollbackOneTime) {
        const rollback = await addCartLine({
          variantId,
          quantity,
          sellingPlanId: rollbackOneTime ? null : rollbackSellingPlanId,
          sections: sectionsParam,
        });

        if (rollback.data) {
          applyCartSectionsResponse(rollback.data, {
            sourceSectionId: sectionId,
            source: `${source}-rollback`,
            skipDepositCleanup: true,
          });
        }
      }

      return;
    }

    if (result.data) {
      applyCartSectionsResponse(result.data, {
        sourceSectionId: sectionId,
        source,
        skipDepositCleanup: true,
      });
    }
  } finally {
    setLineItemUpdating(line, false);
    setCartItemsLocked(false);
  }
}

/** @extends {Component} */
class CartFrequencySelectorComponent extends Component {
  #busy = false;

  async handleSellingPlanChange(event) {
    const select = /** @type {HTMLSelectElement} */ (event.target);
    const isOneTime = select.value === 'one-time';
    const newPlanId = isOneTime ? null : Number(select.value);
    const currentPlanId = Number(this.dataset.currentPlan);

    if (this.#busy) return;
    if (isOneTime && Number.isNaN(currentPlanId)) return;
    if (!isOneTime && (Number.isNaN(newPlanId) || newPlanId === currentPlanId)) return;

    this.#busy = true;
    this.#setLoading(true);

    const line = Number(this.dataset.line);
    const variantId = Number(this.dataset.variantId);
    const quantity = Number(this.dataset.quantity);
    const previousValue = String(currentPlanId);

    try {
      await enqueueCartMutation(() =>
        runCartSellingPlanUpdate(this, {
          source: 'cart-frequency-selector',
          line,
          variantId,
          quantity,
          sellingPlanId: newPlanId,
          rollbackSellingPlanId: isOneTime ? currentPlanId : currentPlanId,
          onErrorReset: () => {
            select.value = previousValue;
          },
        })
      );
    } catch (error) {
      console.error('CartFrequencySelector: Selling plan change failed', error);
      select.value = previousValue;
    } finally {
      this.#busy = false;
      this.#setLoading(false);
    }
  }

  #setLoading(loading) {
    this.classList.toggle('cart-frequency-selector--loading', loading);
    const select = this.querySelector('select');
    if (select) select.disabled = loading;
  }
}

/** @extends {Component} */
class CartLineSubscribeComponent extends Component {
  #busy = false;

  async handleSubscribe(event) {
    event.preventDefault();

    const defaultPlanId = Number(this.dataset.defaultPlan);
    if (!defaultPlanId || this.#busy) return;

    this.#busy = true;
    this.#setLoading(true);

    const line = Number(this.dataset.line);
    const variantId = Number(this.dataset.variantId);
    const quantity = Number(this.dataset.quantity);
    const button = /** @type {HTMLButtonElement | null} */ (this.querySelector('button'));

    try {
      await enqueueCartMutation(() =>
        runCartSellingPlanUpdate(this, {
          source: 'cart-line-subscribe',
          line,
          variantId,
          quantity,
          sellingPlanId: defaultPlanId,
          rollbackOneTime: true,
        })
      );
    } catch (error) {
      console.error('CartLineSubscribe: Subscribe failed', error);
      if (button) button.disabled = false;
    } finally {
      this.#busy = false;
      this.#setLoading(false);
    }
  }

  #setLoading(loading) {
    this.classList.toggle('cart-line-subscribe--loading', loading);
    const button = this.querySelector('button');
    if (button) button.disabled = loading;
  }
}

if (!customElements.get('cart-frequency-selector-component')) {
  customElements.define('cart-frequency-selector-component', CartFrequencySelectorComponent);
}

if (!customElements.get('cart-line-subscribe-component')) {
  customElements.define('cart-line-subscribe-component', CartLineSubscribeComponent);
}
